import React from 'react';
import { X, FileText, Calendar, Check } from 'lucide-react';
import { format } from 'date-fns';
import type { TermsOfService } from '../types/api';

interface TermsModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onAccept?: () => void;
  terms: TermsOfService | null;
  isAcceptMode?: boolean;
  isLoading?: boolean;
  canClose?: boolean;
}

export const TermsModal: React.FC<TermsModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  terms,
  isAcceptMode = false,
  isLoading = false,
  canClose = true,
}) => {
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const acceptButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const titleId = React.useId();

  React.useEffect(() => {
    if (!isOpen || !terms) return;

    const previousFocus = document.activeElement as HTMLElement | null;
    const focusTarget = canClose ? closeButtonRef.current : acceptButtonRef.current;
    window.setTimeout(() => focusTarget?.focus(), 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && canClose && onClose) {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) {
        return;
      }

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
      );

      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus?.();
    };
  }, [canClose, isOpen, onClose, terms]);

  if (!isOpen || !terms) return null;

  const effectiveDate = new Date(terms.effective_date);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm p-4 flex items-center justify-center z-50 animate-fade-in">
      <div
        ref={dialogRef}
        className="bg-white dark:bg-secondary-900 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            <div>
              <h2 id={titleId} className="text-xl font-semibold text-secondary-900 dark:text-white">
                Terms of Service
              </h2>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-0.5">
                Version {terms.version} • Effective {format(effectiveDate, 'MMMM d, yyyy')}
              </p>
            </div>
          </div>
          {canClose && onClose && (
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full text-secondary-500 transition-colors hover:bg-secondary-100 hover:text-secondary-700 dark:text-secondary-400 dark:hover:bg-secondary-800 dark:hover:text-secondary-200"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {terms.summary_of_changes && terms.summary_of_changes !== 'Initial version' && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 p-4">
            <div className="flex items-start gap-2">
              <Calendar className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                  What's Changed
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  {terms.summary_of_changes}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex-grow overflow-y-auto px-6 py-4">
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <div className="whitespace-pre-wrap text-secondary-700 dark:text-secondary-300">
              {terms.content}
            </div>
          </div>
        </div>

        {isAcceptMode && (
          <div className="border-t border-secondary-200 dark:border-secondary-700 p-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                {!canClose
                  ? 'You must accept the terms to continue using the application.'
                  : 'Please review and accept the terms to continue.'}
              </p>
              <div className="flex gap-3">
                {canClose && onClose && (
                  <button onClick={onClose} className="btn btn-secondary" disabled={isLoading}>
                    Cancel
                  </button>
                )}
                <button
                  ref={acceptButtonRef}
                  onClick={onAccept}
                  disabled={isLoading}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Accepting...</span>
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      <span>I Accept</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
